import { normalize } from 'normalizr'
import { Schemas } from '@redux/schema'
import { deleteEntity, updateEntities } from '@redux/entities'

import api from '@functions/api'

import type { PriceListItemOptionValueModel } from '@models/types'
import type { AppDispatch } from '@redux/store'
import type {
  Action, DeleteParams, EntityOptions, Errors, FetchRequestPayload, RootReducerState, UpdateParams,
} from '@redux/modules/types'

import { handleError } from '../utils'

const FETCH_REQUEST = 'realbase/priceListItemOptionValue/FETCH_REQUEST'
const FETCH_SUCCESS = 'realbase/priceListItemOptionValue/FETCH_SUCCESS'
const FETCH_FAILURE = 'realbase/priceListItemOptionValue/FETCH_FAILURE'

const CREATE_REQUEST = 'realbase/priceListItemOptionValue/CREATE_REQUEST'
const CREATE_SUCCESS = 'realbase/priceListItemOptionValue/CREATE_SUCCESS'
const CREATE_FAILURE = 'realbase/priceListItemOptionValue/CREATE_FAILURE'

const UPDATE_REQUEST = 'realbase/priceListItemOptionValue/UPDATE_REQUEST'
const UPDATE_SUCCESS = 'realbase/priceListItemOptionValue/UPDATE_SUCCESS'
const UPDATE_FAILURE = 'realbase/priceListItemOptionValue/UPDATE_FAILURE'

const DELETE_REQUEST = 'realbase/priceListItemOptionValue/DELETE_REQUEST'
const DELETE_SUCCESS = 'realbase/priceListItemOptionValue/DELETE_SUCCESS'
const DELETE_FAILURE = 'realbase/priceListItemOptionValue/DELETE_FAILURE'

export type PriceListItemOptionValueModuleState = {
  creating: boolean,
  deleting: boolean,
  errors: string[],
  loaded: boolean,
  loadedForKeys: string[],
  loadedIds: number[],
  loading: boolean,
  updating: boolean,
}

// Initial State
const initialState: PriceListItemOptionValueModuleState = {
  creating: false,
  deleting: false,
  errors: [],
  loaded: false,
  loadedIds: [],
  loadedForKeys: [],
  loading: false,
  updating: false,
}

// Actions
export function fetchRequest(payload: FetchRequestPayload = {}) {
  return {
    type: FETCH_REQUEST,
    loadedForKeys: payload.loadedForKeys,
    loadedIds: payload.loadedIds,
  }
}

export function fetchSuccess() {
  return {
    type: FETCH_SUCCESS,
  }
}

export function fetchFailure(errors: Errors = []) {
  return {
    type: FETCH_FAILURE,
    errors,
  }
}

export function createRequest() {
  return {
    type: CREATE_REQUEST,
  }
}

export function createSuccess() {
  return {
    type: CREATE_SUCCESS,
  }
}

export function createFailure(errors: Errors = []) {
  return {
    type: CREATE_FAILURE,
    errors,
  }
}

export function updateRequest() {
  return {
    type: UPDATE_REQUEST,
  }
}

export function updateSuccess() {
  return {
    type: UPDATE_SUCCESS,
  }
}

export function updateFailure(errors: Errors = []) {
  return {
    type: UPDATE_FAILURE,
    errors,
  }
}

export function deleteRequest() {
  return {
    type: DELETE_REQUEST,
  }
}

export function deleteSuccess() {
  return {
    type: DELETE_SUCCESS,
  }
}

export function deleteFailure(errors: Errors = []) {
  return {
    type: DELETE_FAILURE,
    errors,
  }
}

// Action Creators
export function loadPriceListItemOptionValues(options: EntityOptions = {}) {
  const { entityKey } = options

  return (dispatch: AppDispatch, getState: () => RootReducerState) => {
    const loadedForKeys = [...getState().priceListItemOptionValues.loadedForKeys]

    if (entityKey && !loadedForKeys.includes(entityKey)){
      loadedForKeys.push(entityKey)
    }

    dispatch(fetchRequest({ loadedForKeys }))

    const promise = api('/price-list-item-option-values', options)
      .then(({ data }: { data: { data: PriceListItemOptionValueModel[] } }) => {
        const normalizedJson = normalize(data.data, Schemas.PRICE_LIST_ITEM_OPTION_VALUE_ARRAY)
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return { success: true as const, data: data.data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function loadPriceListItemOptionValue(priceListItemOptionValueId: number, options: EntityOptions = {}) {
  const { entityKey } = options

  return (dispatch: AppDispatch, getState: () => RootReducerState) => {
    const state = getState()
    const loadedIds = [...state.priceListItemOptionValues.loadedIds]
    const loadedForKeys = [...state.priceListItemOptionValues.loadedForKeys]

    if (priceListItemOptionValueId && !loadedIds.includes(priceListItemOptionValueId)){
      loadedIds.push(priceListItemOptionValueId)
    }

    if (entityKey && !loadedForKeys.includes(entityKey)){
      loadedForKeys.push(entityKey)
    }

    // Set Loading
    dispatch(fetchRequest({ loadedForKeys, loadedIds }))

    const promise = api(`/price-list-item-option-values/${priceListItemOptionValueId}`, options)
      .then(({ data }: { data: PriceListItemOptionValueModel }) => {
        const normalizedJson = normalize(data, Schemas.PRICE_LIST_ITEM_OPTION_VALUE)
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function createPriceListItemOptionValue(priceListItemOptionValue: Partial<PriceListItemOptionValueModel>, options: EntityOptions = {}) {
  const config = {
    method: 'POST',
    data: JSON.stringify({
      ...priceListItemOptionValue,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(createRequest())

    const promise = api('/price-list-item-option-values', options, config)
      .then(({ data }: { data: PriceListItemOptionValueModel }) => {
        const normalizedJson = normalize(data, Schemas.PRICE_LIST_ITEM_OPTION_VALUE)
        dispatch(updateEntities(normalizedJson))
        dispatch(createSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(createFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function updatePriceListItemOptionValue(
  priceListItemOptionValue: UpdateParams<PriceListItemOptionValueModel>,
  options: EntityOptions = {},
) {
  const config = {
    method: 'PUT',
    data: JSON.stringify({
      ...priceListItemOptionValue,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(updateRequest())

    const promise = api(`/price-list-item-option-values/${priceListItemOptionValue.id}`, options, config)
      .then(({ data }: { data: PriceListItemOptionValueModel }) => {
        const normalizedJson = normalize(data, Schemas.PRICE_LIST_ITEM_OPTION_VALUE)
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(updateFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export type BulkCreateParams = {
  priceListItemOptionId: number,
  productOptionValueIds: number[],
}

export function bulkCreatePriceListItemOptionValues(
  params: BulkCreateParams,
  options: EntityOptions = {},
) {
  const config = {
    method: 'POST',
    data: JSON.stringify(params),
  }

  return (dispatch: AppDispatch) => {
    dispatch(createRequest())

    const promise = api('/price-list-item-option-values/bulk', options, config)
      .then(({ data }: { data: PriceListItemOptionValueModel[] }) => {
        const normalizedJson = normalize(data, Schemas.PRICE_LIST_ITEM_OPTION_VALUE_ARRAY)
        dispatch(updateEntities(normalizedJson))
        dispatch(createSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(createFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function deletePriceListItemOptionValue(priceListItemOptionValue: DeleteParams<PriceListItemOptionValueModel>) {
  const config = {
    method: 'DELETE',
  }

  return (dispatch: AppDispatch) => {
    dispatch(deleteRequest())

    const promise = api(`/price-list-item-option-values/${priceListItemOptionValue.id}`, {}, config)
      .then(({ status }) => {
        if (status !== 200) throw new Error()

        const deletedEntity = { id: priceListItemOptionValue.id }
        const normalizedJson = normalize(deletedEntity, Schemas.PRICE_LIST_ITEM_OPTION_VALUE)
        dispatch(deleteEntity(normalizedJson))
        dispatch(deleteSuccess())

        return { success: true as const, data: deletedEntity }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(deleteFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function removePriceListItemOptionValue(
  dispatch: AppDispatch,
  priceListItemOptionValue: PriceListItemOptionValueModel,
) {
  const normalizedJson = normalize(priceListItemOptionValue, Schemas.PRICE_LIST_ITEM_OPTION_VALUE)
  dispatch(deleteEntity(normalizedJson))
}

export function setDefaultPriceListItemOptionValue(
  priceListItemOptionValueId: number,
  isDefault: boolean,
  options: EntityOptions = {},
) {
  const config = {
    method: 'PUT',
  }

  return (dispatch: AppDispatch) => {
    dispatch(updateRequest())
    const priceListItemOptionValueRoute = `/price-list-item-option-values/${priceListItemOptionValueId}`
    const promise = api(`${priceListItemOptionValueRoute}/default?isDefault=${isDefault}`, options, config)
      .then(({ data }: { data: PriceListItemOptionValueModel }) => {
        const normalizedJson = normalize(data, Schemas.PRICE_LIST_ITEM_OPTION_VALUE_ARRAY)

        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(updateFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

// Reducer
export default function reducer(
  state: PriceListItemOptionValueModuleState = initialState,
  action: Action = { type: '' },
): PriceListItemOptionValueModuleState {
  switch (action.type){
    case FETCH_REQUEST:
      return {
        ...state,
        loadedForKeys: action.loadedForKeys || state.loadedForKeys,
        loadedIds: action.loadedIds || state.loadedIds,
        loading: true,
      }
    case FETCH_SUCCESS:
      return {
        ...state,
        loading: false,
        loaded: true,
        errors: [],
      }
    case FETCH_FAILURE:
      return {
        ...state,
        loading: false,
        loaded: false,
        errors: action.errors || [],
      }
    case CREATE_REQUEST:
      return {
        ...state,
        creating: true,
      }
    case CREATE_SUCCESS:
      return {
        ...state,
        creating: false,
        errors: [],
      }
    case CREATE_FAILURE:
      return {
        ...state,
        creating: false,
        errors: action.errors || [],
      }
    case UPDATE_REQUEST:
      return {
        ...state,
        updating: true,
      }
    case UPDATE_SUCCESS:
      return {
        ...state,
        updating: false,
        errors: [],
      }
    case UPDATE_FAILURE:
      return {
        ...state,
        updating: false,
        errors: action.errors || [],
      }
    case DELETE_REQUEST:
      return {
        ...state,
        deleting: true,
      }
    case DELETE_SUCCESS:
      return {
        ...state,
        deleting: false,
        errors: [],
      }
    case DELETE_FAILURE:
      return {
        ...state,
        deleting: false,
        errors: action.errors || [],
      }
    default:
      return state
  }
}
